import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Checkbox } from "@carbon/react";
import { Add } from "@carbon/icons-react";
import DataTable, {
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import MaterialListFilters from "./MaterialListFilters";
import {
  listHeaders,
  mapListItemToRow,
  mapSearchItemToRow,
  materialRowAccent,
  searchHeaders,
} from "./materialRows";
import useDebounce from "../../hooks/useDebounce";
import { useSites } from "../../hooks/useSites";
import {
  useAllMaterials,
  useMaterials,
  useSearchMaterials,
} from "../../hooks/useMaterials";
import { useGetTagsInUse } from "../../hooks/useTags";
import { queryKeys } from "../../hooks/queryKeys";
import { useUser } from "../../context";
import { MaterialType, TagResponse } from "../../types";
import "./materials.scss";

/**
 * Rebuilt on the Carbon primitives layer (Task 5) on `PageHeader`/`DataTable`/`EmptyState`,
 * following `containers/Sites/Sites.tsx`/`containers/Tags/Tags.tsx` as the reviewed
 * list-screen shape. Replaces the pre-Carbon `MaterialsTable` (507 lines, MUI +
 * material-react-table + Tailwind, one branch per material-type/search/owner/company
 * combination) with a single Carbon `DataTable` instance whose `rows`/`headers` swap between
 * "list mode" (server pagination via `useAllMaterials`/`useMaterials`, `MaterialWithSuppliersResponse`
 * rows carrying supplier/order counters) and "search mode" (`useSearchMaterials`, flat
 * `MaterialResponse` rows, no counters) — see `materialRows.tsx` for why those two modes need
 * different row/header shapes.
 *
 * Server-side search + pagination, same law `Sites.tsx`/`Users.tsx` already document: the
 * `query`/`page` params always go to the backend via the existing `useMaterials.ts` hooks — never
 * a client-side filter of an already-fetched page (the Users-list lesson: that would silently
 * shorten a page instead of excluding a row from the real result set).
 *
 * Row actions: the pre-Carbon list only ever had ONE — the row's own name, linking to
 * `/materials/:id` (a second, purely decorative chevron-only link to the same href existed too;
 * dropped here as the e2e safety net's own "UN-DRIVABLE SURFACES" note documents it carries no
 * distinct behaviour). Edit/delegate/toggle-activate all live on the Material DETAIL screen
 * (`components/Material/Actions/Actions.tsx`), out of this task's LIST-only scope.
 */
const Materials = () => {
  const { t } = useTranslation();
  const { user, companyList } = useUser();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 400);
  const [selectedTags, setSelectedTags] = useState<TagResponse[]>([]);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  // The list is company-wide but defaults to the logged-in user's own materials; clearing the
  // owner filter shows everyone. Materials sits behind auth, so `user` is already resolved here.
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>(
    user?.id ? [user.id] : []
  );
  const [materialType, setMaterialType] = useState<MaterialType>(
    MaterialType.ALL
  );

  const tagIds = useMemo(
    () => selectedTags.map((tag) => tag.id),
    [selectedTags]
  );

  // A new search, type, tag or company filter always restarts at the first page — same
  // convention Sites.tsx/Users.tsx/Tags.tsx use. `tagIds`/`selectedCompanyIds` are safe effect
  // deps here: `tagIds` is a `useMemo` keyed off `selectedTags` (stable unless that state
  // actually changes) and `selectedCompanyIds` is itself the state array (only a new reference
  // when `setSelectedCompanyIds` is actually called) — neither creates a new reference on every
  // render, so this can't loop.
  useEffect(() => {
    setPage(0);
  }, [
    debouncedSearchTerm,
    materialType,
    tagIds,
    selectedCompanyIds,
    selectedOwnerIds,
  ]);

  const { data: sites } = useSites(user?.company?.id || "");
  // The tag filter only offers tags that are actually assigned to a material (see useGetTagsInUse).
  const { data: availableTags } = useGetTagsInUse();

  const isSearching = Boolean(debouncedSearchTerm);

  const { data: allMaterials, isLoading: isLoadingAll } = useAllMaterials(
    materialType,
    page,
    tagIds,
    selectedCompanyIds,
    selectedOwnerIds,
    pageSize
  );
  const { data: materials, isLoading: isLoadingByType } = useMaterials(
    queryKeys.MATERIALS,
    materialType,
    page,
    tagIds,
    selectedCompanyIds,
    selectedOwnerIds,
    pageSize
  );
  const { data: foundMaterials, isLoading: isLoadingSearch } =
    useSearchMaterials(
      debouncedSearchTerm,
      page,
      tagIds,
      selectedCompanyIds,
      selectedOwnerIds,
      pageSize
    );

  const activeListData =
    materialType === MaterialType.ALL ? allMaterials : materials;
  const isLoading = isSearching
    ? isLoadingSearch
    : materialType === MaterialType.ALL
      ? isLoadingAll
      : isLoadingByType;

  const rows = useMemo(() => {
    if (isSearching) {
      return (foundMaterials?.content ?? []).map((material) =>
        mapSearchItemToRow(material)
      );
    }
    return (activeListData?.content ?? []).map((item) =>
      // The unified list is company-wide, so show the Owner column to tell whose material it is.
      mapListItemToRow(item, t, true)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching, foundMaterials, activeListData, t]);

  const headers = isSearching ? searchHeaders(t) : listHeaders(t, true);
  const totalItems = isSearching
    ? (foundMaterials?.totalElements ?? 0)
    : (activeListData?.totalElements ?? 0);

  const toggleCompany = (companyId: string) => {
    setSelectedCompanyIds((prev) =>
      prev.includes(companyId)
        ? prev.filter((id) => id !== companyId)
        : [...prev, companyId]
    );
  };

  const addAction = (
    <Button as={Link} to="/materials/new" renderIcon={Add}>
      {t("materials.add")}
    </Button>
  );

  // A company with no delivery place yet can't upload a good/service at all — same gate the
  // pre-Carbon screen had, rebuilt on PageHeader/EmptyState rather than raw Tailwind markup.
  if (sites?.length === 0) {
    return (
      <div>
        <PageHeader title={t("materials.title")} />
        <EmptyState
          title={t("materials.description")}
          action={
            <Button as={Link} to="/sites/new" renderIcon={Add}>
              {t("materials.addSite")}
            </Button>
          }
        />
      </div>
    );
  }

  // Genuinely nothing to show yet, as opposed to a search that merely narrowed the roster to
  // nothing — same `isTrulyEmpty` convention Sites.tsx/Users.tsx/Tags.tsx use.
  const isTrulyEmpty = !isLoading && rows.length === 0 && !isSearching;

  const empty = isSearching ? (
    <EmptyState
      title={t("materials.noResults")}
      action={
        <Button kind="ghost" onClick={() => setSearchTerm("")}>
          {t("table.clearSearch")}
        </Button>
      }
    />
  ) : (
    <EmptyState title={t("materials.noUploaded")} action={addAction} />
  );

  return (
    <div>
      <PageHeader
        title={t("materials.title")}
        action={!isTrulyEmpty ? addAction : null}
      />

      <MaterialListFilters
        idPrefix="materials"
        materialType={materialType}
        onMaterialTypeChange={setMaterialType}
        availableTags={availableTags ?? []}
        selectedTags={selectedTags}
        onTagsChange={setSelectedTags}
        selectedOwnerIds={selectedOwnerIds}
        onOwnerChange={setSelectedOwnerIds}
      />

      {companyList && companyList.length > 1 ? (
        <fieldset className="materials__company-filter">
          <legend>{t("purchaseRequests.filters.companies")}</legend>
          {companyList.map((company) => (
            <Checkbox
              key={company.id}
              id={`materials-company-${company.id}`}
              labelText={company.name}
              checked={selectedCompanyIds.includes(company.id)}
              onChange={() => toggleCompany(company.id)}
            />
          ))}
        </fieldset>
      ) : null}

      <DataTable
        headers={headers}
        rows={rows}
        isLoading={isLoading}
        search={{
          value: searchTerm,
          onChange: setSearchTerm,
          label: t("materials.search"),
        }}
        rowAccent={materialRowAccent}
        pagination={{
          // DataTable's Pagination (Carbon) is 1-indexed; this screen's own paging state — and
          // the list/search queries it drives — stays 0-indexed to match the API, same boundary
          // conversion Sites.tsx/Users.tsx/Tags.tsx use. Page size is now a wired control: the
          // backend honours a `size` query param on the Materials list/search endpoints, so a
          // change from the items-per-page dropdown flows through `pageSize` state into the hooks.
          page: page + 1,
          pageSize,
          pageSizes: PAGE_SIZES,
          totalItems,
          onPageChange: (newPage, newPageSize) => {
            setPage(newPage - 1);
            setPageSize(newPageSize);
          },
        }}
        empty={empty}
      />
    </div>
  );
};

export default Materials;
